import { isWithinInterval, parseISO, startOfDay } from 'date-fns';

const millisecondsInADay = 86_400_000;

/**
 * Detects whether a date string is a "naive" wall-clock timestamp — i.e. it
 * has no timezone indicator (no trailing `Z`, no `+HH:MM` / `-HH:MM` offset).
 *
 * Examples of naive strings: "2026-08-17T19:00:00", "2026-08-17"
 * Examples of non-naive strings: "2026-08-17T19:00:00Z", "2026-08-17T19:00:00+05:30"
 */
export const isNaiveTimestamp = (value: string): boolean =>
  !/(?:Z|[+-]\d{2}:\d{2})$/i.test(value.trim());

const shouldUseTwelveHourClock = (language: string) =>
  language.toLowerCase().startsWith('en');

export const formatLocalizedTimeRange = (
  startDate: Date | null,
  endDate: Date | null,
  language: string,
) => {
  if (!startDate || !endDate) {
    return '';
  }

  const formatter = new Intl.DateTimeFormat(language, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: shouldUseTwelveHourClock(language),
  });

  if (typeof formatter.formatRange === 'function') {
    return formatter.formatRange(startDate, endDate);
  }

  return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
};

export const daysSince = (sinceDate?: string): number => {
  if (!sinceDate) {
    return 1;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const waitingSince = new Date(sinceDate);
  waitingSince.setHours(0, 0, 0, 0);
  return Math.round(
    (today.getTime() - waitingSince.getTime()) / millisecondsInADay,
  );
};

/**
 * Calculate the number of calendar days from one date until another.
 *
 * For naive (offset-free) timestamp strings the comparison uses **local**
 * calendar dates so that "2026-08-17T19:00:00" always represents August 17
 * regardless of the runtime timezone. For timezone-aware strings (with `Z` or
 * an offset) the comparison uses UTC calendar dates.
 *
 * The time of day is ignored, and the calculation uses calendar dates to
 * avoid daylight-saving transitions producing fractional-day results.
 *
 * @param targetDate The date being counted toward.
 * @param fromDate The date to count from. Defaults to the current day.
 * @returns A positive number for a future date, zero for the same day, a
 * negative number for a past date, or `undefined` for an invalid date.
 */
export const daysUntil = (
  targetDate?: string | Date | null,
  fromDate: string | Date = new Date(),
): number | undefined => {
  if (!targetDate) {
    return;
  }

  const target = targetDate instanceof Date ? targetDate : new Date(targetDate);
  const from = fromDate instanceof Date ? fromDate : new Date(fromDate);

  if (Number.isNaN(target.getTime()) || Number.isNaN(from.getTime())) {
    return;
  }

  // Use local calendar fields when the target is a naive (offset-free) string.
  // This ensures wall-clock dates like "2026-08-17T19:00:00" always represent
  // August 17 regardless of runtime timezone. The fromDate (whether a Date
  // object like new Date() or another naive string) is also compared using
  // local fields to keep the comparison consistent.
  const useLocal =
    typeof targetDate === 'string' &&
    isNaiveTimestamp(targetDate) &&
    (typeof fromDate !== 'string' || isNaiveTimestamp(fromDate));

  const targetDay = useLocal
    ? Date.UTC(target.getFullYear(), target.getMonth(), target.getDate())
    : Date.UTC(
        target.getUTCFullYear(),
        target.getUTCMonth(),
        target.getUTCDate(),
      );
  const fromDay = useLocal
    ? Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())
    : Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());

  return Math.round((targetDay - fromDay) / millisecondsInADay);
};

export const toIsoString = (date?: string | Date): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

/**
 * Converts a date to an ISO date string.
 * @param date The Date object to convert.
 * @returns A date string in the following format: yyyy-mm-dd.
 */
export const toIsoDateString = (date?: string | Date): string | undefined => {
  if (!date) {
    return;
  }

  const isoString = toIsoString(date);
  if (!isoString) {
    return;
  }

  return isoString.split('T')[0];
};

/**
 * Converts a date to a 2-digit month and year.
 * @param date The Date object to convert.
 * @returns A date string in the following format: mm/yy.
 */
export const toTwoDigitDateString = (date?: Date): string | undefined => {
  if (!date) {
    return;
  }

  const options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    year: '2-digit',
  };

  return new Intl.DateTimeFormat('en-US', options).format(date);
};

/** Generate select items for the 12 calendar months. */
export const generateMonthItems = () =>
  Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    return {
      value: month.toString(),
      // TODO: locale-aware month names
      text: Intl.DateTimeFormat('en', { month: 'long' }).format(
        new Date(2000, i),
      ),
    };
  });

/** Generate select items for days 1–31. */
export const generateDayItems = () =>
  Array.from({ length: 31 }, (_, i) => {
    const day = i + 1;
    return {
      value: day.toString(),
      text: day.toString(),
    };
  });

/**
 * Formats a date string or Date object into a short human-readable format.
 * @param date - ISO date string or Date object
 * @returns Formatted string (e.g. "Jun 2, 2026") or undefined if invalid
 */
export const toShortDateString = (
  date?: string | Date | null,
): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats a date string or Date object into a long human-readable format with time.
 * @param date - ISO date string or Date object
 * @returns Formatted string (e.g. "June 2, 2026, 5:34 PM") or undefined if invalid
 */
export const toLongDate12hrTimeString = (
  date?: string | Date | null,
): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(parsed);
};

/**
 * Formats a date to short time only (e.g. "8:00 AM").
 * Returns undefined for empty or invalid input.
 */
export const toShortTimeString = (
  date?: string | Date | null,
): string | undefined => {
  if (!date) {
    return undefined;
  }
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const toCalendarDay = (date: string | Date): Date | undefined => {
  const parsed = typeof date === 'string' ? parseISO(date) : date;
  return Number.isNaN(parsed.getTime()) ? undefined : startOfDay(parsed);
};

/**
 * Checks if a date falls within a calendar-day range (inclusive). Comparison is
 * day-granular and timezone-safe (see `toCalendarDay`).
 * @param startDate - Start of the range. Omit for an open-ended start.
 * @param endDate - End of the range. Omit for an open-ended end.
 * @param dateToCheck - The date to compare against the range. Defaults to today.
 * @returns True if the date is within the range, false otherwise.
 */
export const isCalendarDayInRange = (
  startDate?: string | Date,
  endDate?: string | Date,
  dateToCheck: string | Date = new Date(),
): boolean => {
  const current = toCalendarDay(dateToCheck);
  if (!current) {
    return false;
  }

  const start = startDate === undefined ? undefined : toCalendarDay(startDate);
  const end = endDate === undefined ? undefined : toCalendarDay(endDate);

  if ((startDate !== undefined && !start) || (endDate !== undefined && !end)) {
    return false;
  }

  if (start && end) {
    return start > end ? false : isWithinInterval(current, { start, end });
  }

  if (start) {
    return current >= start;
  }
  if (end) {
    return current <= end;
  }
  return true;
};

/** Parses an `MM-DD` string into a comparable `month * 100 + day` number. */
const toMonthDayNumber = (monthDay: string): number | undefined => {
  const match = /^(\d{2})-(\d{2})$/.exec(monthDay.trim());
  if (!match) {
    return;
  }

  const month = Number(match[1]);
  const day = Number(match[2]);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return;
  }

  return month * 100 + day;
};

/**
 * Checks if a date falls within a recurring, year-agnostic month/day window
 * (inclusive) — e.g. an appeal that runs every year from Nov 1 to Dec 20.
 * Ranges that wrap the year boundary are supported (e.g. `'12-20'`–`'01-05'`).
 * @param startMonthDay - Start of the window as `'MM-DD'`.
 * @param endMonthDay - End of the window as `'MM-DD'`.
 * @param dateToCheck - The date to test. Defaults to today.
 * @returns True if the date is within the recurring window, false otherwise.
 */
export const isMonthDayWithinRange = (
  startMonthDay: string,
  endMonthDay: string,
  dateToCheck: Date = new Date(),
): boolean => {
  const start = toMonthDayNumber(startMonthDay);
  const end = toMonthDayNumber(endMonthDay);
  if (start === undefined || end === undefined) {
    return false;
  }

  const current = (dateToCheck.getMonth() + 1) * 100 + dateToCheck.getDate();

  // Non-wrapping window (e.g. 11-01 .. 12-20).
  if (start <= end) {
    return current >= start && current <= end;
  }

  // Window wraps the year boundary (e.g. 12-20 .. 01-05).
  return current >= start || current <= end;
};

/**
 * Checks whether two date/time ranges overlap.
 * Two ranges overlap when one starts before the other ends and vice versa.
 */
export const doDateRangesOverlap = (
  startA: string | Date,
  endA: string | Date,
  startB: string | Date,
  endB: string | Date,
): boolean => {
  const a0 = new Date(startA).getTime();
  const a1 = new Date(endA).getTime();
  const b0 = new Date(startB).getTime();
  const b1 = new Date(endB).getTime();
  return a0 < b1 && a1 > b0;
};

/**
 * Formats a date with the given Intl options.
 * When `utc` is true, forces UTC timezone to prevent hydration mismatches
 * for dates that include a Z offset.
 * When `utc` is false (default), treats the date as a wall-clock value.
 */
export const formatDate = (
  date: Date,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US',
  { utc = false }: { utc?: boolean } = {},
): string =>
  date.toLocaleDateString(locale, {
    ...options,
    ...(utc && { timeZone: 'UTC' }),
  });

/**
 * Formats a date to short time (e.g. "7:00 PM").
 * When `utc` is true, formats in UTC timezone.
 * When `utc` is false (default), formats as wall-clock time.
 */
export const formatTime = (
  date: Date,
  locale = 'en-US',
  { utc = false }: { utc?: boolean } = {},
): string =>
  date.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    ...(utc && { timeZone: 'UTC' }),
  });

/**
 * Formats a date/time string or Date to short time.
 * When `utc` is true, formats in UTC timezone (for date strings with Z offset).
 * When `utc` is false (default), formats as wall-clock time (for bare date strings).
 * Returns an empty string for empty, null, or invalid input.
 * E.g. "7:00 PM"
 */
export const formatSlotTime = (
  date: string | Date | null | undefined,
  locale = 'en-US',
  { utc = false }: { utc?: boolean } = {},
): string => {
  if (!date) {
    return '';
  }
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    ...(utc && { timeZone: 'UTC' }),
  });
};

/**
 * Formats a date string or Date for display.
 * When `utc` is true, formats in UTC timezone (for date-key strings like YYYY-MM-DD).
 * When `utc` is false (default), formats as wall-clock date (for bare datetime strings).
 *
 * - 'long': "Saturday, September 26" (for date group headers)
 * - 'short': "Sat, Sep 26" (for inline confirmation display)
 * - 'includeYear': appends the year when true
 */
export const formatSlotDate = (
  date: string | Date,
  style: 'long' | 'short' = 'long',
  includeYear = false,
  locale = 'en-US',
  { utc = false }: { utc?: boolean } = {},
): string => {
  const parsedDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  const options: Intl.DateTimeFormatOptions =
    style === 'short'
      ? { weekday: 'short', month: 'short', day: 'numeric' }
      : { weekday: 'long', month: 'long', day: 'numeric' };
  return parsedDate.toLocaleDateString(locale, {
    ...options,
    ...(includeYear && { year: 'numeric' }),
    ...(utc && { timeZone: 'UTC' }),
  });
};

/**
 * Formats a date range for display.
 * When `utc` is true, uses UTC timezone for date comparison and formatting.
 * When `utc` is false (default), treats dates as wall-clock values.
 *
 * @returns e.g. "March 12, 2026", "March 12 – March 14, 2026", "December 2025 – January 2026"
 */
export const formatDateRange = (
  start: string | Date,
  end: string | Date,
  options?: {
    /** Use month-only format (no day). Useful for tour/multi-month ranges. */
    monthOnly?: boolean;
    utc?: boolean;
  },
): string => {
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);
  const monthOnly = options?.monthOnly ?? false;
  const utc = options?.utc ?? false;
  const tzOpt: { utc?: boolean } = { utc };

  const isSameDay =
    formatDate(startDate, {}, 'en-US', tzOpt) ===
    formatDate(endDate, {}, 'en-US', tzOpt);
  const isSameYear = utc
    ? startDate.getUTCFullYear() === endDate.getUTCFullYear()
    : startDate.getFullYear() === endDate.getFullYear();

  if (monthOnly) {
    const opts: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
    const startStr = formatDate(startDate, opts, 'en-US', tzOpt);
    const endStr = formatDate(endDate, opts, 'en-US', tzOpt);
    return startStr === endStr ? startStr : `${startStr} – ${endStr}`;
  }

  const endOpts: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };

  if (isSameDay) {
    return formatDate(startDate, endOpts, 'en-US', tzOpt);
  }

  const startOpts: Intl.DateTimeFormatOptions = isSameYear
    ? { month: 'long', day: 'numeric' }
    : { month: 'long', day: 'numeric', year: 'numeric' };

  return `${formatDate(startDate, startOpts, 'en-US', tzOpt)} – ${formatDate(endDate, endOpts, 'en-US', tzOpt)}`;
};

/**
 * Formats a date or date range for event-detail display.
 * When `utc` is true, uses UTC timezone. When false (default), wall-clock.
 *
 * - Single day: "Thu • March 12, 2026 • 7:00 PM"
 * - Multi-day (same year): "Thu, March 12 – Sat, March 14, 2026"
 * - Multi-day (cross year): "Thu, December 30, 2025 – Sat, January 1, 2026"
 */
export const formatEventDate = (
  start: string | Date,
  end: string | Date,
  { utc = false }: { utc?: boolean } = {},
): string => {
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);
  const tzOpt: { utc?: boolean } = { utc };

  const isSameDay =
    formatDate(startDate, {}, 'en-US', tzOpt) ===
    formatDate(endDate, {}, 'en-US', tzOpt);

  if (isSameDay) {
    const weekday = formatDate(startDate, { weekday: 'short' }, 'en-US', tzOpt);
    const fullDate = formatDate(
      startDate,
      { month: 'long', day: 'numeric', year: 'numeric' },
      'en-US',
      tzOpt,
    );
    return `${weekday} \u2022 ${fullDate} \u2022 ${formatTime(startDate, 'en-US', tzOpt)}`;
  }

  const isSameYear = utc
    ? startDate.getUTCFullYear() === endDate.getUTCFullYear()
    : startDate.getFullYear() === endDate.getFullYear();
  const startOpts: Intl.DateTimeFormatOptions = isSameYear
    ? { weekday: 'short', month: 'long', day: 'numeric' }
    : { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' };
  const endOpts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };

  return `${formatDate(startDate, startOpts, 'en-US', tzOpt)} – ${formatDate(endDate, endOpts, 'en-US', tzOpt)}`;
};

/**
 * Formats an event time range.
 * When `utc` is true, uses UTC timezone. When false (default), wall-clock.
 *
 * @returns e.g. "7:00 PM – 8:30 PM", "11:00 AM – 1:00 PM"
 */
export const formatEventTime = (
  start: string | Date,
  end: string | Date,
  locale = 'en-US',
  { utc = false }: { utc?: boolean } = {},
): string => {
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return '';
  }

  const startTime = formatTime(startDate, locale, { utc });
  const endTime = formatTime(endDate, locale, { utc });

  return `${startTime} – ${endTime}`;
};
